import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, School, Download, CheckCircle, AlertTriangle } from 'lucide-react';

const PMPSchoolsDetail = () => {
  const { inspectionId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInspection();
  }, [inspectionId]);

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API}/pmp-schools/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspection(response.data);
    } catch (error) {
      toast.error('Failed to load inspection');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'satisfactory') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'needs_attention') return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    return <AlertTriangle className="h-5 w-5 text-red-600" />;
  };

  const getStatusLabel = (status) => {
    if (status === 'satisfactory') return 'Satisfactory';
    if (status === 'needs_attention') return 'Needs Attention';
    return 'Unsatisfactory';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!inspection) {
    return <div className="flex items-center justify-center py-12">Inspection not found</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/field/pmp-schools')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{inspection.school_name}</h1>
            <p className="text-gray-600">Inspection #: {inspection.inspection_number}</p>
          </div>
          {inspection.signature && (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Signed
            </Badge>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Inspection Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">School Name</p>
              <p className="font-medium">{inspection.school_name}</p>
            </div>
            {inspection.campus_name && (
              <div>
                <p className="text-sm text-gray-500">Campus</p>
                <p className="font-medium">{inspection.campus_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Inspection Date</p>
              <p className="font-medium">{_fmtDate(inspection.date_of_inspection)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Inspector</p>
              <p className="font-medium">{inspection.inspector_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Area Type</p>
              <p className="font-medium capitalize">{inspection.area_type.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted</p>
              <p className="font-medium">{_fmtDateTime(inspection.submitted_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inspection Items */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Inspection Points ({inspection.inspection_items?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {inspection.inspection_items && inspection.inspection_items.map((item, idx) => (
              <div key={idx} className={`border rounded-lg p-4 ${item.status !== 'satisfactory' ? 'bg-yellow-50 border-yellow-200' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    {getStatusIcon(item.status)}
                    <div className="flex-1">
                      <p className="font-medium">{item.item}</p>
                      {item.description && (
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant={item.status === 'satisfactory' ? 'default' : 'secondary'}>
                    {getStatusLabel(item.status)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Additional Notes */}
      {inspection.additional_notes && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{inspection.additional_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Forward to Operations */}
      {inspection.forward_to_operations && (
        <Card className="mb-6 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Badge variant="outline" className="text-orange-600">
                Forwarded to Operations
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      {inspection.photos && inspection.photos.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Photos ({inspection.photos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {inspection.photos.map((photo, idx) => (
                <div key={idx} className="border rounded-lg p-2">
                  <img src={photo} alt={`Inspection photo ${idx + 1}`} className="w-full h-40 object-cover rounded" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={() => navigate('/field/pmp-schools')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button>
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>
    </div>
  );
};

export default PMPSchoolsDetail;
